import { FC, ReactNode } from 'react'

import * as LabelPrimitive from '@radix-ui/react-label'
import { styled } from 'styled-components'

import Tooltip from 'core/components/lib/Tooltip'
import { onSurface, onSurfaceVariant, outline } from 'core/styles'

type FormLabelProps = {
  text: ReactNode
  htmlFor?: string
  className?: string
  trailingContent?: ReactNode
  tooltipContent?: ReactNode
  isRequired?: boolean
}

export const Label: FC<FormLabelProps> = ({
  text,
  htmlFor,
  className,
  trailingContent,
  tooltipContent,
  isRequired,
}) => (
  <LabelContainer className={className} htmlFor={htmlFor}>
    <Row>
      <LabelText>
        {text}
        {isRequired ?
          <RequiredIndicator>*</RequiredIndicator>
        : null}
      </LabelText>{' '}
      {!!tooltipContent && <Tooltip content={<TooltipContent>{tooltipContent}</TooltipContent>} iconSize='16px' />}
    </Row>
    {trailingContent == null ? null : (
      <LabelText>
        <TrailingContent>{trailingContent}</TrailingContent>
      </LabelText>
    )}
  </LabelContainer>
)

export const FormContainer = styled.form`
  display: flex;
  flex-flow: row wrap;
  gap: 24px;

  > * {
    flex: 1 1 100%;
    min-width: 0;
  }
`

export const FormRow = styled.div<{ $variant?: 'horizontal' | 'vertical'; $gap?: string }>`
  display: flex;
  flex-direction: ${(p) => (p.$variant === 'horizontal' ? 'row' : 'column')};
  gap: ${(p) => p.$gap ?? '0'};
  align-items: ${(p) => (p.$variant === 'horizontal' ? 'start' : 'stretch')};
  justify-content: ${(p) => (p.$variant === 'horizontal' ? 'space-between' : 'flex-start')};
  width: 100%;

  ${(p) =>
    p.$variant === 'vertical' &&
    `
      > * {
        width: 100%;
      }
    `}

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }
`

const LabelContainer = styled(LabelPrimitive.Root)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 2px;
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`

export const LabelText = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${onSurfaceVariant};
  font-size: 13px;
  font-weight: 500;
`

const TooltipContent = styled.div`
  font-size: 14px;
`

const TrailingContent = styled.div`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  margin-left: 8px;
  color: ${onSurfaceVariant};
  font-size: 11px;
  font-weight: 600;
`

export const FormValue = styled.span<{ $variant?: 'default' | 'notification' | 'light' }>`
  font-size: 14px;
  ${(p) =>
    p.$variant === 'notification' ?
      `
      color: ${onSurfaceVariant};
      font-weight: 500;
    `
    : p.$variant === 'light' ?
      `
      opacity: 0.8;
      color: ${onSurfaceVariant};
      font-weight: 400;
    `
    : `
      color: ${onSurface};
      font-weight: 600;
    `}
`

export const FieldLabel = styled.label<{ $variant?: 'default' | 'light' }>`
  display: block;
  padding-bottom: 2px;
  color: ${onSurface};
  font-size: 13px;
  font-weight: 500;

  ${(p) =>
    p.$variant === 'light' &&
    `
      font-size: 14px;
      font-weight: 400;
    `}
`

export type FieldValueProps = {
  $variant?: 'default' | 'compact'
  children?: ReactNode
  className?: string
}

/** Read-only value box. `$variant='compact'` matches `TextInput variant='compact'`. */
export const FieldValue: FC<FieldValueProps> = ({ $variant, children, className }) => (
  <FieldValueBox $variant={$variant} className={className}>
    {$variant === 'compact' ?
      <FieldValueTruncated>{children}</FieldValueTruncated>
    : children}
  </FieldValueBox>
)

const RequiredIndicator = styled.span`
  margin-left: 4px;
`

const FieldValueBox = styled.div<{ $variant?: 'default' | 'compact' }>`
  box-sizing: border-box;
  border: 1px solid ${outline};
  border-radius: 8px;
  color: ${onSurface};
  font-size: 14px;

  ${(p) =>
    p.$variant === 'compact' ?
      `
    padding: 0 12px;
    height: 32px;
    font-weight: 500;
    display: flex;
    align-items: center;
    min-width: 0;

    &:hover {
      border-color: ${outline};
    }
  `
    : `
    padding: 10px 12px;
  `}
`

/** Block child so `text-overflow: ellipsis` applies (not reliable on the flex container itself). */
const FieldValueTruncated = styled.span`
  display: block;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
